import { sanitizeDocumentHtml } from '../../utils/documentHtml';
import { propertyText } from './kmlHelpers';

// A file can set extendedDataKeys itself, so anything but a list of names gives no rows.
const keyListOf = (raw: string): string[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * The InfoWindow options for a clicked feature, or null when it has nothing to show: its name as the header, and as
 * the content its body as sanitized HTML (the <BalloonStyle> text, else the <description>) above a table of the
 * file's own <ExtendedData> values as text. parseKml already sanitizes both bodies; they are sanitized again here as
 * defence in depth, as this puts the body into the page as markup and reads it through whatever getter it is handed.
 */
export function infoWindowOptionsOf(get: (key: string) => unknown): google.maps.InfoWindowOptions | null {
  const name = propertyText(get('name'));
  const bodyHtml =
    sanitizeDocumentHtml(propertyText(get('balloonText'))) || sanitizeDocumentHtml(propertyText(get('description')));

  // Only the file's own <Data> keys, listed before any key was injected, so no injected property shows as a row.
  const extraRows: Array<[string, string]> = keyListOf(propertyText(get('extendedDataKeys')))
    .map((key): [string, string] => [key, propertyText(get(key))])
    .filter(([, val]) => val !== '');

  if (!name && !bodyHtml && extraRows.length === 0) return null;

  // The name goes in the title bar beside the close button, as KmlLayer showed it, not in a blank row above the body.
  const headerEl = name ? Object.assign(document.createElement('strong'), { textContent: name }) : undefined;
  const bodyEl = document.createElement('div');
  bodyEl.innerHTML = bodyHtml;
  if (extraRows.length > 0) bodyEl.appendChild(extendedDataTable(extraRows));

  return { headerContent: headerEl, content: bodyEl.children.length || bodyEl.textContent ? bodyEl : undefined };
}

/** A balloon's table of <ExtendedData> values, a row per key with the key as its header; both go in as text. */
export function extendedDataTable(rows: ReadonlyArray<readonly [string, string]>): HTMLTableElement {
  const table = document.createElement('table');
  table.style.cssText = 'border-collapse:collapse;margin-top:6px;font-size:13px;width:100%';
  rows.forEach(([key, val]) => {
    const row = table.insertRow();
    const keyCell = Object.assign(document.createElement('th'), { scope: 'row', textContent: key });
    keyCell.style.cssText =
      'font-weight:bold;padding:2px 8px 2px 0;vertical-align:top;white-space:nowrap;text-align:left';
    row.appendChild(keyCell);
    const valueCell = row.insertCell();
    valueCell.style.cssText = 'padding:2px 0;vertical-align:top';
    valueCell.textContent = val;
  });
  return table;
}
